/**
 * Style Theme Schema for Flow2
 *
 * This file defines the visual styling layer that applies colors, fonts,
 * and other visual properties to content.
 *
 * @packageDocumentation
 * @module ai/schemas
 */

import { ContentBlockType, ScreenType } from './ContentBlock';
import { ScreenDecoration } from '../styles/themeDecorations';
import Color from '../../model/Color';

/**
 * Color definition (can be hex, rgb, or rgba)
 */
export type ColorValue = string;

/**
 * Color palette for a theme
 */
export interface ThemeColors {
  /** Primary brand color */
  primary: ColorValue;
  /** Secondary brand color */
  secondary: ColorValue;
  /** Background color */
  background: ColorValue;
  /** Primary text color */
  textPrimary: ColorValue;
  /** Secondary/muted text color */
  textSecondary: ColorValue;
  /** Accent colors for highlights */
  accent: ColorValue[];
  /** Success color */
  success?: ColorValue;
  /** Warning color */
  warning?: ColorValue;
  /** Error color */
  error?: ColorValue;
}

/**
 * Typography settings for a theme
 */
export interface ThemeTypography {
  /** Font for titles and headings */
  titleFont: string;
  /** Font for body text */
  bodyFont: string;
  /** Base font size */
  baseFontSize?: number;
  /** Font weight for titles */
  titleWeight?: 'normal' | 'bold' | 'light';
  /** Font weight for body */
  bodyWeight?: 'normal' | 'bold' | 'light';
  /** Line height multiplier */
  lineHeight?: number;
}

/**
 * Spacing settings for a theme
 */
export interface ThemeSpacing {
  /** Base spacing unit in pixels */
  unit: number;
  /** Screen padding */
  screenPadding: number;
  /** Gap between elements */
  elementGap: number;
  /** Padding inside text boxes */
  textPadding?: number;
}

/**
 * Border definition for block and media styling
 */
export interface BorderStyle {
  /** Border color (hex) */
  color?: ColorValue;
  /** Border width in pixels */
  width?: number;
  /** Border radius in pixels (corner rounding) */
  radius?: number;
}

/**
 * Style overrides for specific content block types
 */
export interface BlockStyleOverrides {
  /** Font size */
  fontSize?: number;
  /** Font name */
  fontName?: string;
  /** Text color */
  textColor?: ColorValue;
  /** Background color */
  backgroundColor?: ColorValue;
  /** Background gradient (linear or radial) */
  backgroundGradient?: {
    type: 'linear' | 'radial';
    angle?: number;
    stops: { color: ColorValue; offset: number }[];
  };
  /** Text alignment */
  textAlignment?: 'left' | 'center' | 'right';
  /** Line height */
  lineHeight?: number;
  /** Letter spacing */
  letterSpacing?: number;
  /** Font weight */
  fontWeight?: 'normal' | 'bold' | 'light';
  /** Text transform */
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  /** Border radius (deprecated - use border.radius) */
  borderRadius?: number;
  /** Border styling */
  border?: BorderStyle;
  /** Padding */
  padding?: number | { top: number; right: number; bottom: number; left: number };
}

/**
 * Style overrides for media objects (images, PDFs, videos, galleries)
 */
export interface MediaStyleOverrides {
  /** Border styling */
  border?: BorderStyle;
  /** Corner radius in pixels (convenience alias for border.radius) */
  cornerRadius?: number;
  /** Drop shadow */
  shadow?: {
    color?: ColorValue;
    blur?: number;
    offsetX?: number;
    offsetY?: number;
  };
  /** Opacity (0-1) */
  opacity?: number;
}

/**
 * Slot-level style overrides for a specific layout
 * Allows themes to customize how specific slots render in specific layouts
 */
export interface SlotStyleOverride {
  /** Font size override */
  fontSize?: number;
  /** Font name override */
  fontName?: string;
  /** Text color override */
  textColor?: ColorValue;
  /** Text alignment override */
  textAlignment?: 'left' | 'center' | 'right';
  /** Letter spacing override */
  letterSpacing?: number;
  /** Line height override */
  lineHeight?: number;
}

/**
 * Layout-specific style customizations
 * Each key is a slot ID within the layout
 */
export interface LayoutStyleOverrides {
  /** Slot-specific style overrides (key = slotId, e.g., 'main-title', 'body') */
  slots?: Record<string, SlotStyleOverride>;
  /** Additional decoration elements for this layout */
  decorationIndex?: number;
}

/**
 * Layout preferences that allow a theme to influence layout selection.
 * This is the thin coupling between the Style and Layout layers —
 * a score boost, not a hard override.
 */
export interface LayoutPreferences {
  /** Layout IDs that get a score boost when this theme is active */
  preferredLayoutIds?: string[];
  /** Per-screenType layout overrides (strong preference) */
  screenTypeLayouts?: Partial<Record<ScreenType, string>>;
  /** Per-layout style customizations (key = layoutId) */
  layoutStyles?: Record<string, LayoutStyleOverrides>;
}

/**
 * Media types that can have style overrides
 */
export type MediaType = 'image' | 'video' | 'pdf' | 'gallery';

/**
 * Style theme definition
 */
export interface StyleTheme {
  /** Unique identifier */
  id: string;
  /** Theme name */
  name: string;
  /** Theme description */
  description?: string;
  /** Color palette */
  colors: ThemeColors;
  /** Typography settings */
  typography: ThemeTypography;
  /** Spacing settings */
  spacing: ThemeSpacing;
  /** Style overrides by block type */
  blockStyles: Partial<Record<ContentBlockType, BlockStyleOverrides>>;
  /** Style overrides for media objects (images, PDFs, videos, galleries) */
  mediaStyles?: Partial<Record<MediaType, MediaStyleOverrides>>;
  /** FlowVella theme ID for export */
  flowvellaThemeId?: string;
  /** FlowVella theme string ID */
  flowvellaThemeStringId?: string;
  /** Category for filtering */
  category?: 'light' | 'dark' | 'colorful' | 'minimal' | 'professional';
  /** Preview image URL */
  previewUrl?: string;
  /** Is this a system theme */
  isSystem?: boolean;
  /** Tags for searching */
  tags?: string[];
  /** Visual decoration layers (background images, shape overlays, gradients) */
  decorations?: ScreenDecoration[];
  /** Layout preferences, thin coupling from Style → Layout selection */
  layoutPreferences?: LayoutPreferences;
}

/**
 * Default spacing configuration
 */
export const DEFAULT_SPACING: ThemeSpacing = {
  unit: 8,
  screenPadding: 40,
  elementGap: 20,
  textPadding: 10,
};

/**
 * Default typography configuration
 */
export const DEFAULT_TYPOGRAPHY: ThemeTypography = {
  titleFont: 'HelveticaNeue-Bold',
  bodyFont: 'HelveticaNeue',
  baseFontSize: 28,
  titleWeight: 'bold',
  bodyWeight: 'normal',
  lineHeight: 1.4,
};

/**
 * Create a style theme
 */
export function createStyleTheme(
  id: string,
  name: string,
  colors: ThemeColors,
  options?: {
    typography?: Partial<ThemeTypography>;
    spacing?: Partial<ThemeSpacing>;
    blockStyles?: Partial<Record<ContentBlockType, BlockStyleOverrides>>;
    mediaStyles?: Partial<Record<MediaType, MediaStyleOverrides>>;
    flowvellaThemeId?: string;
    flowvellaThemeStringId?: string;
    category?: StyleTheme['category'];
    description?: string;
    tags?: string[];
    decorations?: ScreenDecoration[];
    layoutPreferences?: LayoutPreferences;
  }
): StyleTheme {
  return {
    id,
    name,
    description: options?.description,
    colors,
    typography: {
      ...DEFAULT_TYPOGRAPHY,
      ...options?.typography,
    },
    spacing: {
      ...DEFAULT_SPACING,
      ...options?.spacing,
    },
    blockStyles: options?.blockStyles || {},
    mediaStyles: options?.mediaStyles,
    flowvellaThemeId: options?.flowvellaThemeId,
    flowvellaThemeStringId: options?.flowvellaThemeStringId,
    category: options?.category,
    isSystem: true,
    tags: options?.tags,
    decorations: options?.decorations,
    layoutPreferences: options?.layoutPreferences,
  };
}

/**
 * Get block style with theme defaults applied
 */
export function getBlockStyle(
  theme: StyleTheme,
  blockType: ContentBlockType
): BlockStyleOverrides {
  const baseStyle: BlockStyleOverrides = {
    fontName: theme.typography.bodyFont,
    fontSize: theme.typography.baseFontSize,
    textColor: theme.colors.textPrimary,
    lineHeight: theme.typography.lineHeight,
  };

  // Apply type-specific defaults - optimized for widescreen presentations
  switch (blockType) {
    case 'title':
      baseStyle.fontName = theme.typography.titleFont;
      baseStyle.fontSize = 56;
      baseStyle.fontWeight = theme.typography.titleWeight;
      break;
    case 'subtitle':
      baseStyle.fontName = theme.typography.titleFont;
      baseStyle.fontSize = 40;
      baseStyle.textColor = theme.colors.textSecondary;
      break;
    case 'body':
      baseStyle.fontSize = 28;
      baseStyle.lineHeight = 1.5;
      break;
    case 'bullet-list':
    case 'numbered-list':
      baseStyle.fontSize = 28;
      baseStyle.lineHeight = 1.6;
      break;
    case 'quote':
      baseStyle.fontName = 'Georgia-Italic';
      baseStyle.fontSize = 44;
      baseStyle.textAlignment = 'center';
      break;
    case 'callout':
      baseStyle.backgroundColor = theme.colors.accent[0];
      baseStyle.padding = 16;
      baseStyle.borderRadius = 8;
      break;
    case 'caption':
      baseStyle.fontSize = 20;
      baseStyle.textColor = theme.colors.textSecondary;
      break;
  }

  // Merge with theme-specific overrides
  const themeOverrides = theme.blockStyles[blockType] || {};
  return { ...baseStyle, ...themeOverrides };
}

/**
 * Convert a hex color to RGB object
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Convert RGB to hex color
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Convert color to FlowVella iOS format (RRGGBBAA)
 */
export function colorToIOSFormat(color: ColorValue, alpha: number = 1): string {
  const rgb = hexToRgb(color);
  if (!rgb) return '000000FF';
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${rgb.r.toString(16).padStart(2, '0')}${rgb.g.toString(16).padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}${alphaHex}`;
}

/**
 * Convert a hex color string to a Color object
 */
export function hexToColor(hex: string): Color {
  const rgb = hexToRgb(hex);
  if (!rgb) return new Color(0, 0, 0, 1);
  return new Color(rgb.r, rgb.g, rgb.b, 1);
}

/**
 * Current schema version for styles
 */
export const STYLE_SCHEMA_VERSION = '1.0.0';
